import React from "react";
import { Sparkles, MessageCircle, ChevronDown, MessageSquarePlus } from "lucide-react";
import { motion } from "framer-motion";

const TransformDemoFlow = () => {
  const [containerWidth, setContainerWidth] = React.useState(1200);
  const containerRef = React.useRef(null);

  // Handle responsive breakpoint and container width
  React.useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    // Initial calculation
    updateDimensions();

    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  const getRightwardPath = (startX, startY, endX, endY) => {
    const controlOffset = Math.abs(endX - startX) * 0.3;
    const cp1x = startX + controlOffset;
    const cp1y = startY;
    const cp2x = endX - controlOffset;
    const cp2y = endY;
    return `M ${startX} ${startY} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${endX} ${endY}`;
  };

  // Calculate positions dynamically based on actual container width
  const node1Width = 320;
  const node2Width = 320;
  const gapBetweenNodes = 80; // Gap between the two nodes

  const node1Top = 80;
  const node1Height = 320;
  const node1MiddleY = node1Top + (node1Height / 2);

  const node2Top = 80;
  const node2Height = 400;
  const node2MiddleY = node2Top + (node2Height / 2);

  // Node 1 starts at left edge
  const node1LeftEdge = 0;
  const node1RightEdge = node1LeftEdge + node1Width;

  // Node 2 starts after node 1 + gap
  const node2LeftEdge = node1RightEdge + gapBetweenNodes;

  // Adjust for Node 2 being scaled to 0.7
  const node2Scale = 0.7;
  const node2ScaledWidth = node2Width * node2Scale;
  const node2ScaleOffset = (node2Width - node2ScaledWidth) / 2;
  const node2AdjustedLeftEdge = node2LeftEdge + node2ScaleOffset;

  // Calculate the actual visible left edge of node 2 (accounting for border and positioning)
  const node2VisibleLeftEdge = node2LeftEdge + 4; // +4 for border width
  const arrowWidth = 10;
  const lineEndX = node2VisibleLeftEdge - arrowWidth - 5; // Stop before arrow so arrow ends 5px before node

  const positions = {
    node1: { className: 'top-20', left: node1LeftEdge, handleX: node1RightEdge, handleY: node1MiddleY },
    node2: { className: 'top-20', left: node2LeftEdge, handleX: node2AdjustedLeftEdge, handleY: node2MiddleY },
    connection1: { path: getRightwardPath(node1RightEdge, node1MiddleY, lineEndX, node2MiddleY) },
  };

  return (
    <div className="relative ml-auto mr-0" style={{ width: '800px', maxWidth: '90vw', marginRight: '0%', transform: 'scale(1.0)', transformOrigin: 'right top' }}>
      <div
        ref={containerRef}
        className="relative w-full pb-[480px]"
        style={{
          perspective: '1000px',
          perspectiveOrigin: 'center center',
          marginTop: '-120px'
        }}>
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-32 h-32 bg-blue-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-40 h-40 bg-purple-400 rounded-full blur-3xl"></div>
        </div>

        {/* 3D Transform wrapper */}
        <div
          className="absolute inset-0"
          style={{
            transformStyle: 'preserve-3d',
            transform: 'rotateY(15deg) rotateZ(-10deg)',
            transformOrigin: 'center center',
          }}>

          {/* SVG for connections with arrow markers */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 1 }}>
          {/* Arrow marker definition */}
          <defs>
            <marker
              id="arrowhead"
              markerWidth="10"
              markerHeight="10"
              refX="9"
              refY="3"
              orient="auto">
              <polygon points="0 0, 10 3, 0 6" fill="#d4af37" />
            </marker>
          </defs>

          {/* Connection 1: First to Second Node - DASHED LINE */}
          <motion.g
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.2 }}>
            <path
              d={positions.connection1.path}
              stroke="#d4af37"
              strokeWidth="2"
              strokeDasharray="8 4"
              strokeLinecap="round"
              fill="none"
              filter="drop-shadow(0 2px 4px rgba(212, 175, 55, 0.3))"
            />
          </motion.g>

          {/* Arrow - Golden triangle pointing to node */}
          <motion.polygon
            points={`${lineEndX},${node2MiddleY - 4} ${lineEndX + 10},${node2MiddleY} ${lineEndX},${node2MiddleY + 4}`}
            fill="#d4af37"
            stroke="#d4af37"
            strokeWidth="2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 2.0 }}
          />

          {/* Connection handles (golden dots) - Animated */}
          <motion.circle
            cx={node1RightEdge}
            cy={node1MiddleY}
            r="6"
            fill="#d4af37"
            stroke="#fff"
            strokeWidth="2"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.4, delay: 1.0, type: "spring", stiffness: 200 }}
          />
          </svg>

          {/* Node 1: First Node - AMBER with START badge */}
          <motion.div
            className={`absolute ${positions.node1.className}`}
            style={{ zIndex: 2, left: `${positions.node1.left}px` }}
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}>
          <div className="bg-white rounded-2xl shadow-lg border-4 border-amber-400 w-80 relative">
            {/* START badge */}
            <motion.div
              className="absolute -top-12 left-0 right-0 flex justify-center"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.6 }}>
              <div className="bg-gradient-to-r from-amber-500 to-yellow-400 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg border-2 border-white">
                START
              </div>
            </motion.div>

            {/* Header */}
            <div className="bg-gradient-to-r from-amber-600 to-amber-600 text-white px-4 py-3 rounded-t-xl">
              <h3 className="font-semibold text-sm text-left">Who was David?</h3>
            </div>

            {/* Content */}
            <div className="p-4">
              <p className="text-xs text-gray-800 leading-relaxed text-left">
                David was the second king of Israel, renowned for his faith and courage. He was born in Bethlehem and started as a humble shepherd boy.
              </p>
              <div className="text-xs text-gray-600 mt-2 text-left relative">
                As a young shepherd, he defeated the giant{" "}
                <span className="relative bg-gradient-to-r from-yellow-200 to-amber-200 px-1 rounded">
                  Goliath
                  {/* Learn More and Ask buttons */}
                  <motion.div
                    className="absolute left-0 top-full mt-1"
                    style={{ zIndex: 10 }}
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.9 }}>
                    <div className="flex gap-2">
                      <button className="inline-flex items-center gap-1 bg-green-500 text-white px-3 py-1.5 rounded-full text-xs font-medium shadow-lg whitespace-nowrap">
                        <Sparkles size={12} />
                        <span>Learn more</span>
                      </button>
                      <button className="inline-flex items-center gap-1 bg-blue-500 text-white px-3 py-1.5 rounded-full text-xs font-medium shadow-lg whitespace-nowrap">
                        <MessageCircle size={12} />
                        <span>Ask</span>
                      </button>
                    </div>
                  </motion.div>
                </span>
                {" "}with just a sling and a stone. Later, he became king and established Jerusalem as the capital of Israel.
              </div>

              {/* More link */}
              <div className="flex justify-end mb-3 mt-2">
                <div className="flex items-center gap-1 text-xs text-blue-600 font-medium">
                  <span>More</span>
                  <ChevronDown size={14} />
                </div>
              </div>

              {/* Ask Input Box */}
              <div className="border-t border-gray-200 pt-3 mt-3">
                <div className="flex gap-2">
                  <div className="relative flex-1 px-3 py-2 text-xs border border-gray-300 rounded-lg bg-white opacity-60 text-left">
                    <span className="text-gray-400">What speaks to your heart?</span>
                  </div>
                  <button
                    disabled
                    className="px-3 py-2 rounded-lg font-medium text-xs bg-gray-100 text-gray-400 cursor-not-allowed flex items-center gap-1">
                    <MessageSquarePlus size={14} />
                    <span>Ask</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
          </motion.div>

          {/* Node 2: Second Node */}
          <motion.div
            className={`absolute ${positions.node2.className}`}
            style={{
              zIndex: 2,
              left: `${positions.node2.left}px`,
              transform: 'scale(0.7)',
              transformOrigin: 'center center'
            }}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 1.5, ease: "easeOut" }}>
          <div className="bg-white rounded-2xl shadow-lg w-80 hover:shadow-xl transition-shadow relative border-4 border-emerald-400">
            {/* Badge - CURRENT */}
            <motion.div
              className="absolute -top-12 left-0 right-0 flex justify-center"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 1.8 }}>
              <div className="bg-gradient-to-r from-emerald-500 to-green-400 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg border-2 border-white">
                CURRENT
              </div>
            </motion.div>

            {/* Header - Green */}
            <div className="text-white px-4 py-3 rounded-t-xl bg-gradient-to-r from-emerald-600 to-emerald-600">
              <h3 className="font-semibold text-sm text-left">Goliath</h3>
            </div>

            {/* Content */}
            <div className="p-4 text-left relative">
              <div className="text-xs text-gray-800 leading-relaxed space-y-2">
                <p className="font-medium">
                  <span className="font-bold">Goliath</span> was a giant Philistine warrior who stood over 9 feet tall and wore heavy bronze armor.
                </p>
                <p className="text-gray-600 text-xs">
                  With just a sling and a stone, young David showed that faith in God can overcome any obstacle. While Goliath mocked him, David proclaimed that the battle belongs to the Lord.
                </p>
                <p className="text-gray-600 text-xs">
                  The stone struck Goliath in the forehead, and he fell to the ground. This victory demonstrated that God doesn't need powerful weapons to achieve His purposes.
                </p>
              </div>

              {/* Ask Input Box */}
              <div className="border-t border-gray-200 pt-3 mt-3">
                <div className="flex gap-2">
                  <div className="relative flex-1 px-3 py-2 text-xs border border-gray-300 rounded-lg bg-white opacity-60 text-left">
                    <span className="text-gray-400">What speaks to your heart?</span>
                  </div>
                  <button
                    disabled
                    className="px-3 py-2 rounded-lg font-medium text-xs bg-gray-100 text-gray-400 cursor-not-allowed flex items-center gap-1">
                    <MessageSquarePlus size={14} />
                    <span>Ask</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default TransformDemoFlow;
